import { useCallback, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer";
import {
  Button,
  ButtonIconWithLabel,
  Icon_ARKit24,
  Platform,
  PlatformAPI,
  useManagedModal,
} from "@shapr3d/frontend-lib";
import type React from "react";
import QRCode from "react-qr-code";

import CloseIcon from "../../../../assets/images/closeIcon.svg";
import { useTracker } from "../../../../hooks/useTracker";
import { Flyout } from "../../../Flyout/Flyout";
import { isARSupportedDevice } from "../../../ModelViewer/useModelViewer";
import { ARNotSupportedModal } from "../ARNotSupportedModal/ARNotSupportedModal";

import style from "./QRCodeFlyout.module.css";

interface QRCodeFlyoutProps {
  disabled?: boolean;
  modelViewer: React.RefObject<ModelViewerElement> | undefined;
  className?: string;
  variant?: "grayscale" | "borderless";
}

const platform = PlatformAPI.getInstance().getPlatform();
const AR_SUPPORTED_PLATFORMS = [Platform.iPad, Platform.iPhone, Platform.Android];

const QRCodeFlyout: React.FC<QRCodeFlyoutProps> = ({
  disabled,
  modelViewer,
  className,
  variant = "grayscale",
}) => {
  const tracker = useTracker();

  const [open, setOpen] = useState(false);
  const arNotSupportedModal = useManagedModal();

  const URLWithQRSource = new URL(window.location.href);
  URLWithQRSource.searchParams.set("source", "qr");

  const onARButtonClick = useCallback(() => {
    tracker.trackButtonClick({ elementId: "activate-ar-button" });
    if (isARSupportedDevice()) {
      modelViewer?.current?.activateAR?.();
    } else {
      if (AR_SUPPORTED_PLATFORMS.includes(platform)) {
        arNotSupportedModal.open();
      } else {
        setOpen((open) => !open);
      }
    }
  }, [tracker, modelViewer, arNotSupportedModal]);

  return (
    <>
      <Flyout
        activator={
          <Button
            className={className}
            disabled={disabled}
            variant={variant}
            onClick={onARButtonClick}
          >
            <ButtonIconWithLabel icon={<Icon_ARKit24 />} label="AR" />
          </Button>
        }
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className={style.container}>
          <div className={style.closeButton}>
            <img src={CloseIcon} onClick={() => setOpen(false)} />
          </div>
          <div className={style.contentContainer}>
            <div className={style.title}>Preview in Augmented Reality</div>
            <QRCode size={160} value={URLWithQRSource.href} />
            <div className={style.disclaimer}>
              Scan the QR code to open this 3D Model on your iOS or Android device.
            </div>
          </div>
        </div>
      </Flyout>
      <ARNotSupportedModal modalProps={arNotSupportedModal.props} />
    </>
  );
};

export default QRCodeFlyout;
